import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Skeleton from '@mui/material/Skeleton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button } from '@/shared/components/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { useAuthStore } from '@/store/auth';
import {
  guideProfileApi,
  guideProfileKeys,
} from '@/features/guide-profile/api/guide-profile.api';
import { GuideAgreementConsent } from '@/features/guide-profile/components/GuideAgreementConsent';
import { ProfilePhotoUpload } from '@/features/onboarding/components/ProfilePhotoUpload';
import { MultiSelectChips } from '@/features/onboarding/components/MultiSelectChips';
import { DestinationsInput } from '@/features/onboarding/components/DestinationsInput';
import { AutosaveIndicator } from '@/features/onboarding/components/AutosaveIndicator';
import { useDraftAutosave } from '@/features/onboarding/hooks/useDraftAutosave';
import {
  GUIDE_SPECIALIZATIONS,
  INDIA_STATES,
  LANGUAGES,
} from '@/features/onboarding/constants';
import { digitsOnlyNational, fromE164India } from '@/shared/utils/phone';
import { ProfileStatus, type GuideProfile } from '@/types';

/**
 * Guide profile completion. The fields sit in accordions so the page is not
 * one long stack. The guide agreement is confirmed here and recorded when
 * they submit, not while the form autosaves.
 */

type SectionId = 'about' | 'expertise' | 'contact' | 'social' | 'agreement';

type FormState = {
  fullName: string;
  bio: string;
  profilePhoto: string;
  yearsOfExperience: string;
  languagesSpoken: string[];
  state: string;
  city: string;
  destinationsServed: string[];
  specializations: string[];
  whatsappNumber: string;
  instagramUrl: string;
  facebookUrl: string;
  linkedinUrl: string;
  youtubeUrl: string;
  emergencyContactName: string;
  emergencyContactNumber: string;
};

const EMPTY: FormState = {
  fullName: '',
  bio: '',
  profilePhoto: '',
  yearsOfExperience: '',
  languagesSpoken: [],
  state: '',
  city: '',
  destinationsServed: [],
  specializations: [],
  whatsappNumber: '',
  instagramUrl: '',
  facebookUrl: '',
  linkedinUrl: '',
  youtubeUrl: '',
  emergencyContactName: '',
  emergencyContactNumber: '',
};

const REQUIRED_FIELDS: (keyof FormState)[] = [
  'fullName',
  'profilePhoto',
  'bio',
  'yearsOfExperience',
  'languagesSpoken',
  'state',
  'city',
  'destinationsServed',
  'specializations',
  'whatsappNumber',
];

const MIN_BIO = 50;

function toFormState(profile: GuideProfile): FormState {
  return {
    fullName: profile.fullName ?? '',
    bio: profile.bio ?? '',
    profilePhoto: profile.profilePhoto ?? '',
    yearsOfExperience:
      profile.yearsOfExperience != null ? String(profile.yearsOfExperience) : '',
    languagesSpoken: profile.languagesSpoken ?? [],
    state: profile.state ?? '',
    city: profile.city ?? '',
    destinationsServed: profile.destinationsServed ?? [],
    specializations: profile.specializations ?? [],
    whatsappNumber: fromE164India(profile.whatsappNumber) ?? '',
    instagramUrl: profile.instagramUrl ?? '',
    facebookUrl: profile.facebookUrl ?? '',
    linkedinUrl: profile.linkedinUrl ?? '',
    youtubeUrl: profile.youtubeUrl ?? '',
    emergencyContactName: profile.emergencyContactName ?? '',
    emergencyContactNumber: fromE164India(profile.emergencyContactNumber) ?? '',
  };
}

/** Local completeness check. The server re-validates on submit regardless. */
function missingRequired(form: FormState): (keyof FormState)[] {
  return REQUIRED_FIELDS.filter((field) => {
    const value = form[field];
    if (Array.isArray(value)) return value.length === 0;
    if (field === 'bio') return value.trim().length < MIN_BIO;
    if (field === 'whatsappNumber') return !/^[6-9]\d{9}$/.test(value.trim());
    return value.toString().trim().length === 0;
  });
}

const SECTIONS: {
  id: SectionId;
  title: string;
  subtitle: string;
  fields: (keyof FormState)[];
}[] = [
  {
    id: 'about',
    title: 'About you',
    subtitle: 'Your photo and introduction — the first things travellers see.',
    fields: ['fullName', 'profilePhoto', 'bio', 'yearsOfExperience'],
  },
  {
    id: 'expertise',
    title: 'Your expertise',
    subtitle: 'Where you guide, which languages you speak, and what you specialise in.',
    fields: ['languagesSpoken', 'state', 'city', 'destinationsServed', 'specializations'],
  },
  {
    id: 'contact',
    title: 'Contact',
    subtitle: 'How travellers and our team reach you once a trip is confirmed.',
    fields: ['whatsappNumber'],
  },
  {
    id: 'social',
    title: 'Social links',
    subtitle: 'All optional — they help travellers get a feel for your work.',
    fields: [],
  },
  {
    id: 'agreement',
    title: 'Guide agreement',
    subtitle: 'Read the agreement, then confirm it before you submit.',
    fields: [],
  },
];

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <Typography
      component="label"
      sx={{ display: 'block', mb: 0.6, color: '#24364d', fontSize: 12.5, fontWeight: 700 }}
    >
      {children}
      {required ? (
        <Box component="span" sx={{ color: '#e11d48' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );
}

function ProfileSection({
  sectionId,
  number,
  title,
  subtitle,
  expanded,
  onToggle,
  attention,
  children,
}: {
  sectionId: SectionId;
  number: number;
  title: string;
  subtitle: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  attention?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      id={`profile-section-${sectionId}`}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: attention ? 'error.light' : '#e3e8ef',
        borderRadius: '12px !important',
        overflow: 'hidden',
        bgcolor: '#fff',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#7b8ba0' }} />}
        sx={{ px: 2, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.25 } }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', pr: 1 }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 28,
              height: 28,
              flex: '0 0 auto',
              borderRadius: '50%',
              bgcolor: attention ? '#fdecec' : '#edf4ff',
              color: attention ? 'error.main' : '#2f7de1',
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {number}
          </Box>
          <Box>
            <Typography sx={{ color: '#102747', fontSize: 15, lineHeight: 1.25, fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 0.3, color: '#7b8ba0', fontSize: 12.5, lineHeight: 1.4 }}>
              {attention ? 'This section still needs a few details.' : subtitle}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2.25 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
}

export function GuideProfileSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isApprovedGuide, canCompleteProfile, guideLifecycleRoute } = useAuth();
  const setMe = useAuthStore((state) => state.setMe);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState('');
  /** Null until the guide toggles the box; otherwise the server's stored acceptance. */
  const [agreementOverride, setAgreementOverride] = useState<boolean | null>(null);
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    about: true,
    expertise: false,
    contact: false,
    social: false,
    agreement: false,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: guideProfileKeys.me(user?.id),
    queryFn: guideProfileApi.getMyProfile,
    enabled: Boolean(user?.id),
    staleTime: 0,
  });

  // Hydrate once, so autosave round-trips never clobber what is being typed.
  useEffect(() => {
    if (data?.profile && !hydrated) {
      setForm(toFormState(data.profile));
      setHydrated(true);
    }
  }, [data, hydrated]);

  const persist = useCallback(async (payload: Partial<FormState>) => {
    const body: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'yearsOfExperience') {
        if (value === '' || value == null) continue;
        body[key] = Number(value);
      } else if (key === 'whatsappNumber' || key === 'emergencyContactNumber') {
        const digits = digitsOnlyNational(String(value ?? ''));
        if (!digits) continue;
        body[key] = `+91${digits}`;
      } else {
        body[key] = value;
      }
    }
    if (Object.keys(body).length === 0) return;
    await guideProfileApi.patchProfile(body as Partial<GuideProfile>);
  }, []);

  const { status: autosaveStatus, queueSave, flush } = useDraftAutosave<FormState>({
    save: persist,
  });

  const update = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K], immediate = false) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      queueSave({ [field]: value } as Partial<FormState>, immediate);
    },
    [queueSave]
  );

  const missing = useMemo(() => missingRequired(form), [form]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      await flush();
      return guideProfileApi.submit({ agreementAccepted: true });
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: guideProfileKeys.all });
      try {
        setMe(await authApi.getMe());
      } catch {
        // The status page refetches regardless.
      }
      navigate('/guide-application/status', { replace: true });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'We could not submit your profile. Please try again.';
      setSubmitError(message);
      setShowErrors(true);
    },
  });

  if (isApprovedGuide) return <Navigate to="/dashboard" replace />;

  // Verification not passed (or already submitted) — the resolver knows where
  // this user belongs, so defer to it rather than deciding here.
  if (hydrated && !canCompleteProfile && guideLifecycleRoute) {
    return <Navigate to={guideLifecycleRoute} replace />;
  }

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Skeleton variant="rounded" height={560} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error">
          We couldn&apos;t load your profile. Please refresh and try again.
        </Alert>
      </Container>
    );
  }

  const err = (field: keyof FormState, message: string) =>
    showErrors && missing.includes(field) ? message : undefined;

  const changesRequired =
    data?.lifecycle.profileStatus === ProfileStatus.CHANGES_REQUIRED;
  const agreementAccepted = data?.lifecycle.agreementAccepted === true;
  const agreementChecked = agreementOverride ?? agreementAccepted;
  const agreementUrl = data?.lifecycle.agreementUrl || '/legal/SLA.pdf';

  const sectionNeedsAttention = (id: SectionId) => {
    if (!showErrors) return false;
    if (id === 'agreement') return !agreementChecked;
    const section = SECTIONS.find((item) => item.id === id);
    return section?.fields.some((field) => missing.includes(field)) ?? false;
  };

  const firstIncomplete = (): SectionId | null => {
    for (const section of SECTIONS) {
      if (section.id === 'agreement') {
        if (!agreementChecked) return 'agreement';
        continue;
      }
      if (section.fields.some((field) => missing.includes(field))) return section.id;
    }
    return null;
  };

  const revealSection = (id: SectionId) => {
    setOpenSections({
      about: id === 'about',
      expertise: id === 'expertise',
      contact: id === 'contact',
      social: id === 'social',
      agreement: id === 'agreement',
    });
    requestAnimationFrame(() => {
      document.getElementById(`profile-section-${id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  };

  const fieldSx = {
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiInputBase-input': { fontSize: 13.5 },
    '& .MuiFormHelperText-root': { fontSize: 11.5, mx: 0, mt: 0.6 },
  };

  const readyToSubmit = firstIncomplete() === null;

  const continueOrSubmit = () => {
    setSubmitError('');
    const next = firstIncomplete();
    if (!next) {
      submitMutation.mutate();
      return;
    }
    const alreadyOpen = openSections[next];
    revealSection(next);
    setShowErrors(alreadyOpen);
  };

  return (
    <Box
      sx={{
        position: { xs: 'relative', lg: 'absolute' },
        inset: { lg: 0 },
        bgcolor: '#f4f7fb',
        overflow: { xs: 'visible', lg: 'hidden' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: { xs: '0 0 auto', lg: '0 auto 0 0' },
          width: { xs: '100%', lg: '50%' },
          height: { xs: 260, lg: '100%' },
          backgroundImage:
            'linear-gradient(90deg, rgba(255,255,255,.97) 0px, rgba(255,255,255,.94) 420px, rgba(255,255,255,.4) 540px, rgba(255,255,255,0) 700px), url("/images/guideOnboarding.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 'auto', lg: '100%' },
          overflow: { lg: 'hidden' },
          px: { xs: 2, sm: 3, lg: 0 },
          pt: { xs: 3, lg: 0 },
          pb: { xs: 'max(env(safe-area-inset-bottom), 24px)', lg: 0 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: { xs: 3, lg: 0 },
            alignItems: { xs: 'start', lg: 'stretch' },
            height: { xs: 'auto', lg: '100%' },
            minHeight: { lg: 0 },
          }}
        >
          <Box
            sx={{
              height: { xs: 'auto', lg: '100%' },
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { xs: 0, lg: 5 },
              py: { xs: 0, lg: 3 },
            }}
          >
            <Box sx={{ maxWidth: 360 }}>
            <Typography
              sx={{
                color: '#5c6b80',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.14em',
              }}
            >
              Complete your guide profile
            </Typography>
            <Typography
              component="h1"
              sx={{
                mt: 1,
                color: '#102747',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: { xs: 34, lg: 42 },
                lineHeight: 1.02,
                letterSpacing: '-0.03em',
                fontWeight: 700,
              }}
            >
              Show travellers
              <Box component="span" sx={{ display: 'block' }}>
                India through
              </Box>
              <Box component="span" sx={{ display: 'block', color: '#2f7de1' }}>
                your eyes
              </Box>
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#46566d', fontSize: 13.5, lineHeight: 1.55 }}>
              This is what travellers see when they find you on Trplin. Share your story,
              experience and the places you know best. Your progress saves automatically as you type.
            </Typography>

            <Box sx={{ mt: 2.5, display: 'grid', gap: 1.5 }}>
              {[
                [<BadgeOutlinedIcon key="badge" />, 'Build your guide identity', 'Showcase your experience and expertise'],
                [<GroupsOutlinedIcon key="groups" />, 'Connect with travellers', 'Help travellers discover a more authentic India'],
                [<MonetizationOnOutlinedIcon key="money" />, 'Set your own rates', 'Choose your guiding services and pricing'],
              ].map(([icon, title, text]) => (
                <Box key={String(title)} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      bgcolor: '#fff7e4',
                      color: '#e8a71d',
                      '& svg': { fontSize: 19 },
                    }}
                  >
                    {icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#152b49', fontSize: 13, fontWeight: 800 }}>
                      {title}
                    </Typography>
                    <Typography sx={{ color: '#657286', fontSize: 11.5 }}>{text}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2.5,
                border: '1px solid #f3e4c2',
                bgcolor: 'rgba(255,250,240,.96)',
              }}
            >
              <FormatQuoteIcon sx={{ color: '#f0c15a', fontSize: 32, opacity: 0.7 }} />
              <Typography
                sx={{
                  color: '#183150',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 16,
                  lineHeight: 1.45,
                }}
              >
                I love sharing the stories of my city with travellers. Trplin helps me connect
                with people who truly appreciate local experiences.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2 }}>
                <Box
                  component="img"
                  src="/images/guideOnboarding.png"
                  alt=""
                  sx={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', objectPosition: '57% center' }}
                />
                <Box>
                  <Typography sx={{ fontSize: 12, color: '#162d4c', fontWeight: 800 }}>Arjun Singh</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <VerifiedIcon sx={{ color: '#35a980', fontSize: 14 }} />
                    <Typography sx={{ fontSize: 10.5, color: '#68778a' }}>Verified Trplin Guide</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{ mt: 1.5, p: 1.75, display: 'flex', gap: 1.5, borderRadius: 2.5, bgcolor: 'rgba(241,246,252,.96)' }}
            >
              <LockOutlinedIcon sx={{ color: '#173c70', fontSize: 23, mt: 0.25 }} />
              <Box>
                <Typography sx={{ color: '#193454', fontSize: 12.5, fontWeight: 800 }}>
                  Your information stays private
                </Typography>
                <Typography sx={{ color: '#66758a', fontSize: 11, lineHeight: 1.45, mt: 0.5 }}>
                  Your personal information is used only for verification and is not displayed
                  publicly to travellers.
                </Typography>
              </Box>
            </Paper>
            </Box>
          </Box>

          <Box
            sx={{
              minHeight: 0,
              height: { lg: '100%' },
              p: { lg: 2.5 },
              display: 'flex',
              flexDirection: 'column',
              overflow: { lg: 'hidden' },
            }}
          >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              flex: { lg: '1 1 auto' },
              minHeight: { lg: 0 },
              height: { xs: 'auto', lg: 'auto' },
              overflowY: { xs: 'visible', lg: 'auto' },
              overscrollBehavior: 'contain',
              borderRadius: 3,
              border: '1px solid #e3e9f1',
              boxShadow: '0 16px 40px rgba(16, 39, 71, 0.06)',
              bgcolor: '#fff',
              '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
            }}
          >
            {changesRequired && data?.lifecycle.adminNotes && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Our team asked for a few updates</AlertTitle>
                {data.lifecycle.adminNotes}
              </Alert>
            )}
            {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

            <ProfileSection
              sectionId="about"
              number={1}
              title="About you"
              subtitle="Your photo and introduction — the first things travellers see."
              expanded={openSections.about}
              attention={sectionNeedsAttention('about')}
              onToggle={(expanded) => setOpenSections((prev) => ({ ...prev, about: expanded }))}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '170px minmax(0, 1fr)' }, gap: 2.5 }}>
                <ProfilePhotoUpload
                  value={form.profilePhoto}
                  onChange={(url) => update('profilePhoto', url, true)}
                  error={err('profilePhoto', 'A profile photo is required')}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                  <Box>
                    <FieldLabel required>Full name</FieldLabel>
                    <TextField
                      placeholder="Your full name"
                      required
                      fullWidth
                      size="small"
                      hiddenLabel
                      value={form.fullName}
                      onChange={(e) => update('fullName', e.target.value)}
                      onBlur={() => void flush()}
                      error={!!err('fullName', 'x')}
                      helperText={err('fullName', 'Your full name is required')}
                      sx={fieldSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel required>About you</FieldLabel>
                    <TextField
                      placeholder="Tell travellers about yourself, your guiding experience and what makes your tours special..."
                      required
                      fullWidth
                      size="small"
                      hiddenLabel
                      multiline
                      minRows={3}
                      value={form.bio}
                      onChange={(e) => update('bio', e.target.value)}
                      onBlur={() => void flush()}
                      error={!!err('bio', 'x')}
                      helperText={
                        err('bio', `Please write at least ${MIN_BIO} characters`) ??
                        `${form.bio.trim().length}/${MIN_BIO} characters minimum`
                      }
                      sx={fieldSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel required>Years of guiding experience</FieldLabel>
                    <TextField
                      placeholder="E.g. 2 years"
                      required
                      type="number"
                      size="small"
                      fullWidth
                      hiddenLabel
                      inputProps={{ min: 0, max: 50 }}
                      value={form.yearsOfExperience}
                      onChange={(e) => update('yearsOfExperience', e.target.value)}
                      onBlur={() => void flush()}
                      error={!!err('yearsOfExperience', 'x')}
                      helperText={
                        err('yearsOfExperience', 'Required') ??
                        'Include informal guiding, temple tours or local experiences'
                      }
                      sx={fieldSx}
                    />
                  </Box>
                </Box>
              </Box>
            </ProfileSection>

            <ProfileSection
              sectionId="expertise"
              number={2}
              title="Your expertise"
              subtitle="Where you guide, which languages you speak, and what you specialise in."
              expanded={openSections.expertise}
              attention={sectionNeedsAttention('expertise')}
              onToggle={(expanded) => setOpenSections((prev) => ({ ...prev, expertise: expanded }))}
            >
              <MultiSelectChips
                label="Languages you speak"
                required
                options={LANGUAGES}
                value={form.languagesSpoken}
                onChange={(value) => update('languagesSpoken', value, true)}
                error={err('languagesSpoken', 'Select at least one language')}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
                <Box>
                  <FieldLabel required>State</FieldLabel>
                  <TextField
                    select
                    hiddenLabel
                    required
                    size="small"
                    fullWidth
                    value={form.state}
                    onChange={(e) => update('state', e.target.value, true)}
                    error={!!err('state', 'x')}
                    helperText={err('state', 'State is required')}
                    inputProps={{ 'aria-label': 'State' }}
                    sx={fieldSx}
                  >
                    <MenuItem value="" disabled>
                      Select state
                    </MenuItem>
                    {INDIA_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <FieldLabel required>City</FieldLabel>
                  <TextField
                    placeholder="Your city"
                    required
                    hiddenLabel
                    size="small"
                    fullWidth
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    onBlur={() => void flush()}
                    error={!!err('city', 'x')}
                    helperText={err('city', 'City is required')}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              <DestinationsInput
                value={form.destinationsServed}
                onChange={(value) => update('destinationsServed', value, true)}
                error={err('destinationsServed', 'Add at least one destination')}
              />
              <MultiSelectChips
                label="Specializations"
                required
                options={GUIDE_SPECIALIZATIONS}
                value={form.specializations}
                onChange={(value) => update('specializations', value, true)}
                error={err('specializations', 'Select at least one specialization')}
              />
            </ProfileSection>

            <ProfileSection
              sectionId="contact"
              number={3}
              title="Contact"
              subtitle="How travellers and our team reach you once a trip is confirmed."
              expanded={openSections.contact}
              attention={sectionNeedsAttention('contact')}
              onToggle={(expanded) => setOpenSections((prev) => ({ ...prev, contact: expanded }))}
            >
              <Box>
                <FieldLabel required>WhatsApp number</FieldLabel>
                <TextField
                  placeholder="10-digit mobile number"
                  required
                  hiddenLabel
                  size="small"
                  fullWidth
                  value={form.whatsappNumber}
                  onChange={(e) => update('whatsappNumber', digitsOnlyNational(e.target.value))}
                  onBlur={() => void flush()}
                  InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
                  error={!!err('whatsappNumber', 'x')}
                  helperText={err('whatsappNumber', 'Enter a valid 10-digit WhatsApp number')}
                  sx={fieldSx}
                />
              </Box>
              <Divider><Chip label="Optional" size="small" /></Divider>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
                <Box>
                  <FieldLabel>Emergency contact name</FieldLabel>
                  <TextField
                    hiddenLabel
                    size="small"
                    fullWidth
                    value={form.emergencyContactName}
                    onChange={(e) => update('emergencyContactName', e.target.value)}
                    onBlur={() => void flush()}
                    sx={fieldSx}
                  />
                </Box>
                <Box>
                  <FieldLabel>Emergency contact number</FieldLabel>
                  <TextField
                    hiddenLabel
                    size="small"
                    fullWidth
                    value={form.emergencyContactNumber}
                    onChange={(e) => update('emergencyContactNumber', digitsOnlyNational(e.target.value))}
                    onBlur={() => void flush()}
                    InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </ProfileSection>

            <ProfileSection
              sectionId="social"
              number={4}
              title="Social links"
              subtitle="All optional — they help travellers get a feel for your work."
              expanded={openSections.social}
              attention={sectionNeedsAttention('social')}
              onToggle={(expanded) => setOpenSections((prev) => ({ ...prev, social: expanded }))}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
                {[
                  ['instagramUrl', 'Instagram URL', <InstagramIcon key="instagram" sx={{ color: '#d74f8a' }} />],
                  ['facebookUrl', 'Facebook URL', <FacebookIcon key="facebook" sx={{ color: '#1877f2' }} />],
                  ['linkedinUrl', 'LinkedIn URL', <LinkedInIcon key="linkedin" sx={{ color: '#0a66c2' }} />],
                  ['youtubeUrl', 'YouTube URL', <YouTubeIcon key="youtube" sx={{ color: '#f00' }} />],
                ].map(([field, label, icon]) => (
                  <Box key={String(field)}>
                    <FieldLabel>{String(label)}</FieldLabel>
                    <TextField
                      size="small"
                      fullWidth
                      hiddenLabel
                      value={form[field as keyof Pick<FormState, 'instagramUrl' | 'facebookUrl' | 'linkedinUrl' | 'youtubeUrl'>]}
                      onChange={(e) => update(field as keyof Pick<FormState, 'instagramUrl' | 'facebookUrl' | 'linkedinUrl' | 'youtubeUrl'>, e.target.value)}
                      onBlur={() => void flush()}
                      placeholder="https://"
                      InputProps={{ startAdornment: <InputAdornment position="start">{icon}</InputAdornment> }}
                      sx={fieldSx}
                    />
                  </Box>
                ))}
              </Box>
            </ProfileSection>

            <ProfileSection
              sectionId="agreement"
              number={5}
              title="Guide agreement"
              subtitle="Read the agreement, then confirm it before you submit."
              expanded={openSections.agreement}
              attention={sectionNeedsAttention('agreement')}
              onToggle={(expanded) => setOpenSections((prev) => ({ ...prev, agreement: expanded }))}
            >
              <GuideAgreementConsent
                embedded
                url={agreementUrl}
                checked={agreementChecked}
                alreadyAccepted={agreementAccepted}
                showError={showErrors && !agreementChecked}
                onCheckedChange={setAgreementOverride}
              />
            </ProfileSection>

            {missing.length > 0 && showErrors && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {missing.length} required {missing.length === 1 ? 'field still needs' : 'fields still need'} your attention above.
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                px: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: '#32a579', fontSize: 21 }} />
                <Box>
                  <Typography sx={{ color: '#304158', fontSize: 12.5, fontWeight: 800 }}>
                    Your progress is saved automatically
                  </Typography>
                  <Typography sx={{ color: '#778496', fontSize: 10.5 }}>
                    You can leave anytime and continue where you left off.
                  </Typography>
                  <AutosaveIndicator status={autosaveStatus} />
                </Box>
              </Box>
              <Button
                intent="primary-dark"
                size="large"
                disabled={submitMutation.isPending}
                onClick={continueOrSubmit}
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 3.5, minWidth: 160, borderRadius: 2, textTransform: 'none' }}
              >
                {submitMutation.isPending
                  ? 'Submitting…'
                  : readyToSubmit
                    ? 'Submit for review'
                    : 'Continue'}
              </Button>
            </Box>
          </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
